// src/pages/TablesBusiness.jsx
import { useEffect, useMemo, useState, useCallback } from "react";
import { Meta } from "../../components/navigation/Helmet";
import DashboardLayout from "../../hocs/layout/DashboardLayout";
import axios from "axios";

/* Iconitos inline para no agregar librerías */
const Chevron = ({ open }) => (
  <svg
    className={`h-5 w-5 transition-transform duration-200 ${open ? "rotate-180" : "rotate-0"}`}
    viewBox="0 0 20 20"
    fill="currentColor"
    aria-hidden="true"
  >
    <path
      fillRule="evenodd"
      d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z"
      clipRule="evenodd"
    />
  </svg>
);

export default function TablesBusiness() {
  const getToken = () => localStorage.getItem("access") || "";
  const API = "/api";
  const BASE = process.env.REACT_APP_API_URL || "";

  const [loading, setLoading] = useState(true);
  const [errMsg, setErrMsg] = useState("");

  // Paginación + filtros
  const [page, setPage] = useState(1);
  const [pageSize] = useState(9);
  const [tableFilter, setTableFilter] = useState("");   // "M1"
  const [statusFilter, setStatusFilter] = useState(""); // "", "PENDING", ...

  const [data, setData] = useState(null);

  // Estado UI: mesas abiertas/cerradas (acordeón)
  const [expanded, setExpanded] = useState(() => new Set()); // guarda table_id abiertos

  const token = useMemo(() => getToken(), []);
  const authHeaders = useMemo(
    () => ({
      Authorization: `JWT ${token}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    }),
    [token]
  );

  const fetchOrders = async () => {
    setLoading(true);
    setErrMsg("");

    try {
      if (!token) {
        setErrMsg("No hay token de autenticación.");
        setLoading(false);
        return;
      }

      const res = await axios.get(`${BASE}${API}/orders/`, {
        headers: authHeaders,
        params: {
          page,
          page_size: pageSize,
          table: tableFilter || undefined,
          status: statusFilter || undefined,
        },
      });

      setData(res.data);
    } catch (error) {
      if (!error?.response) setErrMsg("El servidor no responde.");
      else if (error.response?.status === 400) setErrMsg("Solicitud incorrecta.");
      else if (error.response?.status === 401) setErrMsg("No autorizado.");
      else setErrMsg("Error al obtener los datos.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, tableFilter, statusFilter]);

  // Acciones
  const markServed = async (orderId) => {
    try {
      await axios.patch(
        `${BASE}${API}/orders/${orderId}/status/`,
        { status: "SERVED" },
        { headers: authHeaders }
      );
      fetchOrders();
    } catch {
      alert("No se pudo marcar como enviado.");
    }
  };

  const cancelItem = async (itemId) => {
    try {
      await axios.patch(
        `${BASE}${API}/order-items/${itemId}/cancel/`,
        {},
        { headers: authHeaders }
      );
      fetchOrders();
    } catch {
      alert("No se pudo cancelar el ítem.");
    }
  };

  const releaseTable = async (tableNumber) => {
    if (!window.confirm(`Liberar y archivar la mesa ${tableNumber}?`)) return;
    try {
      await axios.post(
        `${BASE}${API}/tables/${encodeURIComponent(tableNumber)}/release/`,
        {},
        { headers: authHeaders }
      );
      fetchOrders();
    } catch {
      alert(`No se pudo liberar la mesa ${tableNumber}.`);
    }
  };

  const releaseAllOccupiedOnPage = async () => {
    const list = Array.isArray(data?.results?.tables) ? data.results.tables : [];
    const occupied = list.filter((t) => t.is_open && t.is_occupied);
    if (occupied.length === 0) {
      alert("No hay mesas ocupadas en esta página.");
      return;
    }
    if (!window.confirm(`Liberar y archivar ${occupied.length} mesa(s) visibles?`)) return;

    for (const t of occupied) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await axios.post(
          `${BASE}${API}/tables/${encodeURIComponent(t.number)}/release/`,
          {},
          { headers: authHeaders }
        );
      } catch {}
    }
    fetchOrders();
  };

  // Helpers UI
  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  const orderBadge = (status) => {
    const map = {
      PENDING: "bg-amber-100 text-amber-700",
      SERVED: "bg-emerald-100 text-emerald-700",
      CANCELLED: "bg-rose-100 text-rose-700",
    };
    const cls = map[status] || "bg-gray-100 text-gray-700";
    return <span className={`px-2 py-0.5 text-xs rounded-full font-semibold ${cls}`}>{status}</span>;
  };

  const businessName = data?.results?.business ?? "";
  const tablesRaw = Array.isArray(data?.results?.tables) ? data.results.tables : [];
  const hasPrev = !!data?.previous;
  const hasNext = !!data?.next;
  const totalCount = typeof data?.count === "number" ? data.count : 0;

  // Separar mesas: ocupadas vs libres (todas is_open)
  const mesasOcupadas = tablesRaw.filter((t) => t.is_open && t.is_occupied);
  const mesasLibres = tablesRaw.filter((t) => t.is_open && !t.is_occupied);
  const occupiedCount = mesasOcupadas.length;

  const toggleMesa = useCallback((tableId) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(tableId)) next.delete(tableId);
      else next.add(tableId);
      return next;
    });
  }, []);

  const MesaCard = ({ mesa }) => {
    const isOpen = expanded.has(mesa.table_id);
    const countOrders = Array.isArray(mesa.orders) ? mesa.orders.length : 0;

    return (
      <section className="bg-white rounded-xl border shadow-sm">
        {/* Header mesa (botón acordeón) */}
        <button
          type="button"
          onClick={() => toggleMesa(mesa.table_id)}
          className="w-full px-4 py-3 border-b flex items-center justify-between hover:bg-gray-50"
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-full bg-red-100 text-red-600 flex items-center justify-center font-bold">
              {String(mesa.number ?? "?").slice(0, 2)}
            </div>
            <div className="min-w-0">
              <div className="text-base font-semibold text-gray-900 truncate">Mesa {mesa.number}</div>
              <div className="text-xs text-gray-500 truncate">
                {mesa.is_occupied ? "Activa • Ocupada" : "Activa • Libre"}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* badge con # de órdenes cuando está cerrado (y también abierto, por consistencia) */}
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
              {countOrders} {countOrders === 1 ? "pedido" : "pedidos"}
            </span>

            {/* acción rápida: liberar (solo si ocupada) */}
            {mesa.is_occupied && (
              <button
                className="hidden sm:inline px-3 py-1.5 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 text-xs"
                onClick={(e) => { e.stopPropagation(); releaseTable(mesa.number); }}
              >
                Liberar
              </button>
            )}

            <Chevron open={isOpen} />
          </div>
        </button>

        {/* Contenido del acordeón */}
        {isOpen && (
          <div className="px-4 py-4">
            {countOrders > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {mesa.orders.map((o) => (
                  <article key={o.id} className="border rounded-lg p-3 bg-gray-50">
                    {/* Header del pedido compacto con acciones a la derecha */}
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-semibold text-gray-900 truncate">
                          Pedido #{String(o.id).slice(0, 8)}
                        </div>
                        <div className="text-[12px] text-gray-500">
                          Creado: {fmtDate(o.created_at)}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {orderBadge(o.status)}
                        {o.status === "PENDING" ? (
                          <button
                            className="px-2.5 py-1.5 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 text-xs"
                            onClick={() => markServed(o.id)}
                            title="Marcar pedido como enviado"
                          >
                            Enviar
                          </button>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </div>
                    </div>

                    {/* Items del pedido */}
                    {Array.isArray(o.items) && o.items.length > 0 ? (
                      <div className="mt-3 flex flex-col gap-1.5">
                        {o.items.map((it) => (
                          <div
                            key={it.id}
                            className="flex items-center justify-between rounded-md bg-white px-2 py-1"
                          >
                            <div className="text-sm text-gray-800">
                              {it.qty}× {it.name}
                              {it.notes && (
                                <span className="ml-2 text-[11px] text-gray-500">• {it.notes}</span>
                              )}
                              {it.is_cancelled && (
                                <span className="ml-2 text-[11px] font-semibold text-rose-600">
                                  CANCELADO
                                </span>
                              )}
                            </div>
                            {!it.is_cancelled && o.status !== "CANCELLED" && (
                              <button
                                className="px-2 py-1 rounded bg-rose-100 text-rose-700 hover:bg-rose-200 text-[11px]"
                                onClick={() => cancelItem(it.id)}
                                title="Cancelar ítem"
                              >
                                Cancelar
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="mt-3 text-sm text-gray-500">Sin ítems cargados.</div>
                    )}
                  </article>
                ))}
              </div>
            ) : (
              <div className="text-sm text-gray-500">La mesa no tiene pedidos con el filtro actual.</div>
            )}
          </div>
        )}
      </section>
    );
  };

  return (
    <DashboardLayout>
      <Meta title="Mesas y Órdenes • TableChat" />

      {/* Contenedor central */}
      <div className="w-full overflow-x-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Título + botón global */}
          <div className="flex items-center justify-between mb-2">
            <div>
              <h1 className="text-2xl font-bold">Gestión de Mesas</h1>
              {businessName && <p className="text-sm text-gray-500">Negocio: {businessName}</p>}
            </div>

            <button
              className="px-3 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 text-sm disabled:opacity-50"
              onClick={releaseAllOccupiedOnPage}
              disabled={occupiedCount === 0 || loading}
              title="Libera y archiva todas las mesas ocupadas visibles"
            >
              Liberar ocupadas ({occupiedCount})
            </button>
          </div>

          {/* Barra de filtros sticky */}
          <div className="sticky top-16 z-20 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-3 mb-4 bg-white/80 backdrop-blur border-b">
            <div className="flex flex-col md:flex-row gap-3 md:items-end">
              <div className="flex flex-col">
                <label className="text-sm text-gray-600 mb-1">Filtrar por mesa (ej. M1)</label>
                <input
                  className="border rounded px-3 py-2 w-60"
                  placeholder="M1, Terraza-3…"
                  value={tableFilter}
                  onChange={(e) => { setPage(1); setTableFilter(e.target.value.trim()); }}
                />
              </div>

              <div className="flex flex-col">
                <label className="text-sm text-gray-600 mb-1">Estado</label>
                <select
                  className="border rounded px-3 py-2 w-48"
                  value={statusFilter}
                  onChange={(e) => { setPage(1); setStatusFilter(e.target.value); }}
                >
                  <option value="">Todos</option>
                  <option value="PENDING">Pendientes</option>
                  <option value="SERVED">Enviados</option>
                  <option value="CANCELLED">Cancelados</option>
                </select>
              </div>
            </div>
          </div>

          {/* Estado de carga/err */}
          {loading && <p className="text-gray-600">Cargando...</p>}
          {!!errMsg && <p className="text-red-500">{errMsg}</p>}

          {/* CONTENIDO: Mesas ocupadas y libres separadas */}
          {!loading && !errMsg && (
            <>
              {/* Ocupadas */}
              <div className="space-y-4 mb-8">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold">Mesas ocupadas</h2>
                  <span className="text-sm text-gray-500">{occupiedCount} en esta página</span>
                </div>
                {mesasOcupadas.length > 0 ? (
                  mesasOcupadas.map((t) => <MesaCard key={t.table_id} mesa={t} />)
                ) : (
                  <div className="text-sm text-gray-500">No hay mesas ocupadas con el filtro actual.</div>
                )}
              </div>

              {/* Libres */}
              <div className="space-y-4">
                <h2 className="text-lg font-semibold">Mesas libres</h2>
                {mesasLibres.length > 0 ? (
                  mesasLibres.map((t) => <MesaCard key={t.table_id} mesa={t} />)
                ) : (
                  <div className="text-sm text-gray-500">No hay mesas libres con el filtro actual.</div>
                )}
              </div>

              {/* Paginación (sobre MESAS) */}
              <div className="flex items-center gap-3 mt-8">
                <button
                  className="px-3 py-2 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50"
                  disabled={!hasPrev}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Anterior
                </button>
                <span className="text-sm text-gray-700">
                  Página {page}{typeof totalCount === "number" ? ` • ${totalCount} mesas` : ""}
                </span>
                <button
                  className="px-3 py-2 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50"
                  disabled={!hasNext}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Siguiente
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
